/**
 * API Interceptors for Supabase Auth
 * Provides centralized request/response handling with Supabase authentication
 */

import { supabase } from '../lib/supabase';

const isMockAuthEnabled = import.meta.env.VITE_ENABLE_MOCK_AUTH === 'true';

const getMockAuthHeaders = (): Record<string, string> | null => {
  if (!isMockAuthEnabled) {
    return null;
  }

  try {
    const rawUser = window.localStorage.getItem('mock_user');
    if (!rawUser) {
      return null;
    }

    const mockUser = JSON.parse(rawUser) as {
      id?: string;
      companyId?: string;
      orgId?: string;
      role?: string;
      roles?: string[];
      email?: string;
    };

    const userId = mockUser?.id;
    const companyId = mockUser?.companyId || mockUser?.orgId;
    if (!userId || !companyId) {
      return null;
    }

    const role = mockUser?.role || mockUser?.roles?.[0] || 'user';

    const headers: Record<string, string> = {
      'x-mock-user-id': userId,
      'x-mock-company-id': companyId,
      'x-mock-role': role
    };

    if (mockUser.email) {
      headers['x-mock-email'] = mockUser.email;
    }

    return headers;
  } catch (error) {
    // Failed to load mock auth headers
    return null;
  }
};

/**
 * Enhanced fetch function with Supabase auth support
 */
export async function fetchWithInterceptors(
  url: string,
  config: RequestInit = {}
): Promise<Response> {
  try {
    // Prepend API base URL for relative paths
    const API_BASE_URL = import.meta.env.VITE_API_URL || '';
    let fullUrl = url;
    
    // If URL is relative (starts with /), prepend the API base URL
    if (url.startsWith('/') && API_BASE_URL) {
      fullUrl = `${API_BASE_URL}${url}`;
    }
    
    // Get current Supabase session
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      console.error('[API Interceptor] Error getting session:', error);
    }

    // Build headers object with proper type
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Copy existing headers if they exist
    if (config.headers) {
      const existingHeaders = config.headers as Record<string, string>;
      Object.keys(existingHeaders).forEach(key => {
        headers[key] = existingHeaders[key];
      });
    }

    // Add Authorization header if user is authenticated
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    } else {
      // Try to get the session from the user's last sign in
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Try to refresh the session
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshData?.session?.access_token) {
          headers['Authorization'] = `Bearer ${refreshData.session.access_token}`;
        } else if (refreshError) {
          console.error('[API Interceptor] Failed to refresh session:', refreshError);
        }
      }

      if (!headers['Authorization']) {
        const mockHeaders = getMockAuthHeaders();
        if (mockHeaders) {
          Object.assign(headers, mockHeaders);
        }
      }
    }

    // Make the request with Supabase auth
    const response = await fetch(fullUrl, {
      ...config,
      headers,
    });

    return response;
  } catch (error) {
    console.error('[API Error]', error);
    throw error;
  }
}

// Legacy compatibility export
export default { fetchWithInterceptors };

// Remove Auth0 related exports - not needed with Supabase
export function setAccessTokenGetter() {
  // Deprecated - using Supabase auth
}

export function setTokenValidationFailureHandler() {
  // Deprecated - using Supabase auth
}
